import { Fragment, memo } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MoreHorizontal, Play } from 'lucide-react';
import type { Track } from '@pujolisique/shared';
import { usePlayer, currentTrack } from '../player/store';
import { useFavoriteToggle } from '../api/hooks';
import { fmtTime } from '../lib/format';
import { Cover } from './Cover';
import { useTrackMenu } from './trackActions';

interface Props {
  tracks: Track[];
  source?: string;
  showCover?: boolean;
  showAlbum?: boolean;
  /** 'auto' = colonne compositeur si au moins un titre en a un */
  showComposer?: boolean | 'auto';
  numbering?: 'track' | 'index';
  groupDiscs?: boolean;
  onRemove?: (t: Track) => void;
  removeLabel?: string;
}

export function TrackList({ tracks, source, showCover, showAlbum, showComposer = 'auto', numbering = 'index', groupDiscs, onRemove, removeLabel }: Props) {
  const playTracks = usePlayer((s) => s.playTracks);
  const current = usePlayer(currentTrack);
  const playing = usePlayer((s) => s.playing);
  const toggle = usePlayer((s) => s.toggle);
  const menu = useTrackMenu();
  const fav = useFavoriteToggle();
  const withComposer = showComposer === 'auto' ? tracks.some((t) => t.composers.length) : showComposer;
  const discs = groupDiscs ? new Set(tracks.map((t) => t.discNumber ?? 1)).size > 1 : false;

  if (!tracks.length) return null;

  // Colonnes calculées selon les informations affichées (bureau / mobile)
  const cols = ['2.5rem', showCover && '2.75rem', 'minmax(0,1.4fr)', withComposer && 'minmax(0,1fr)', showAlbum && 'minmax(0,1fr)', '2.25rem', '3.5rem', '2.25rem'].filter(Boolean).join(' ');
  const colsMobile = [showCover ? '3rem' : '2rem', 'minmax(0,1fr)', '2.5rem'].join(' ');

  return (
    <div
      className={`tracks ${showCover ? 'tracks--cover' : ''}`}
      role="list"
      style={{ ['--cols' as string]: cols, ['--cols-m' as string]: colsMobile }}
    >
      {tracks.map((t, i) => {
        const isCurrent = current?.id === t.id;
        const newDisc = discs && (i === 0 || (tracks[i - 1].discNumber ?? 1) !== (t.discNumber ?? 1));
        return (
          <Fragment key={`${t.id}-${i}`}>
            {newDisc && <div className="tracks__disc">Disque {t.discNumber ?? 1}</div>}
            <TrackRow
              t={t}
              n={numbering === 'track' ? (t.trackNumber ?? i + 1) : i + 1}
              isCurrent={isCurrent}
              isPlaying={isCurrent && playing}
              showCover={showCover}
              showAlbum={showAlbum}
              withComposer={withComposer}
              onPlay={() => (isCurrent ? toggle() : playTracks(tracks, i, { source }))}
              onMenu={(e) => menu(t, e, { onRemove: onRemove ? () => onRemove(t) : undefined, removeLabel })}
              onFav={() => fav.mutate({ kind: 'track', id: t.id, value: !t.favorite })}
            />
          </Fragment>
        );
      })}
    </div>
  );
}

interface RowProps {
  t: Track;
  n: number;
  isCurrent: boolean;
  isPlaying: boolean;
  showCover?: boolean;
  showAlbum?: boolean;
  withComposer: boolean;
  onPlay: () => void;
  onMenu: (e: { clientX: number; clientY: number } | DOMRect) => void;
  onFav: () => void;
}

const TrackRow = memo(function TrackRow({ t, n, isCurrent, isPlaying, showCover, showAlbum, withComposer, onPlay, onMenu, onFav }: RowProps) {
  return (
    <div
      className={`track ${isCurrent ? 'is-current' : ''}`}
      role="listitem"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('a,button')) return;
        onPlay();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onMenu(e);
      }}
    >
      <button className="track__num" onClick={onPlay} aria-label={isPlaying ? `Mettre en pause ${t.title}` : `Lire ${t.title}`}>
        {isPlaying ? <Bars /> : <span className="track__n">{n}</span>}
        <Play size={15} className="track__play" fill="currentColor" />
      </button>
      {showCover && <Cover art={t.cover} name={t.album ?? t.title} size={64} className="track__cover" />}
      <div className="track__main">
        <span className="track__title">{t.title}</span>
        <span className="track__sub">
          {t.artists.map((a, k) => (
            <Fragment key={a.id || a.name}>
              {k > 0 && ', '}
              {a.id ? <Link to={`/artist/${a.id}`}>{a.name}</Link> : a.name}
            </Fragment>
          ))}
        </span>
      </div>
      {withComposer && (
        <div className="track__composer">
          {t.composers.map((c, k) => (
            <Fragment key={c.id}>
              {k > 0 && ', '}
              {c.id.startsWith('name:') ? c.name : <Link to={`/composer/${c.id}`}>{c.name}</Link>}
            </Fragment>
          ))}
        </div>
      )}
      {showAlbum && <div className="track__album">{t.albumId ? <Link to={`/album/${t.albumId}`}>{t.album}</Link> : t.album}</div>}
      <button className={`icon-btn track__fav ${t.favorite ? 'is-on' : ''}`} onClick={onFav} aria-label={t.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'} aria-pressed={!!t.favorite}>
        <Heart size={16} fill={t.favorite ? 'currentColor' : 'none'} />
      </button>
      <span className="track__dur">{fmtTime(t.duration)}</span>
      <button className="icon-btn track__more" onClick={(e) => onMenu(e.currentTarget.getBoundingClientRect())} aria-label={`Plus d’actions pour ${t.title}`}>
        <MoreHorizontal size={18} />
      </button>
    </div>
  );
});

export function Bars() {
  return (
    <span className="bars" aria-hidden>
      <i />
      <i />
      <i />
    </span>
  );
}
